import {WorkoutBlockType} from "../../constants/enums";
import UseLandscape from "./useLandscape";
import UsePortrait from "./usePortrait";
import BLOCKS from "../../constants/workoutBlocks";

const useDownloadWorkoutAsPDF = () => {
  const downloadWorkout = async (workout, orientation, numberOfBlockPerPage, image, dateStr, workoutName, fontSize) => {
    return new Promise((resolve, reject) => {
      try {
        console.log("Workout", workout);
        const blocks = convertBlocks(workout);
        console.log("Blocks", blocks);

        if (orientation === 'landscape') {
          UseLandscape(blocks, parseInt(numberOfBlockPerPage), image, dateStr, workoutName, fontSize);
        } else {
          UsePortrait(blocks, parseInt(numberOfBlockPerPage), image, dateStr, workoutName, fontSize);
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  return {downloadWorkout}
};

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const convertBlocks = (workout) => {
  return workout.workout.workoutBlocks.map(block => {
    switch (block.type) {
      case WorkoutBlockType.CLASSICSTRENGTH:
        return classicStrengthBlock(block);
      case WorkoutBlockType.PILATESR:
        return pilatesRBlock(block);
      case WorkoutBlockType.INTERVAL:
        return intervalBlock(block);
      case WorkoutBlockType.AMRAP:
        return amrapBlock(block);
      case WorkoutBlockType.RFT:
        return rftBlock(block);
      case WorkoutBlockType.RNFT:
        return rftBlock(block);
      case WorkoutBlockType.STRENGTH:
        return strengthBlock(block);
      case "SUPERSETS":
        return superSetsBlock(block);
      case WorkoutBlockType.EMOM:
        return emomBlock(block);
      case WorkoutBlockType.RUN:
        return runBlock(block);
      case WorkoutBlockType.REST:
        return restBlock(block);
      case WorkoutBlockType.CUSTOM:
        return customBlock(block);
    }
  })
}

const customBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getCustomLines(block)
  }
}

const restBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getRestLines(block)
  }
}

const runBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getRunLines(block)
  }
}

const emomBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getEmomLines(block)
  }
}

const superSetsBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getSuperSetLines(block)
  }
}

const strengthBlock = (block) => {
  return {
    title: getBlockTitle(block) + ` - ${block.exercises[0].exercise.title}`,
    lines: getStrengthLines(block)
  }
}

const rftBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getRftLines(block)
  }
}

const amrapBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getAmrapLines(block)
  }
}

const getCustomLines = (block) => {
  const message = block.customAttributes.find(x => x.name === "text")?.textValue
  let lines = []

    lines.push({
      name: `- Message -`,
      descriptions: message.split("\n")
    });
    return lines;
}

const getRestLines = (block) => {
  const restTime = block.customAttributes.find(x => x.name === "rest")?.value
  let lines = []

  lines.push({
    name: `- Rest ${formatTime(restTime)} -`,
    descriptions: []
  });

  return lines;
}

const getRunLines = (block) => {
  const numberOfRounds = block.customAttributes.find(x => x.name === "rounds")?.value
  let lines = []

  lines.push({
    name: `- ${numberOfRounds} Rounds -`,
    descriptions: []
  });

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.toLowerCase()} ${getRunWeight(exercise)}`]
    })});

  return lines;
}

const getRunWeight = (exercise) => {
  if (!exercise.weight || exercise.weight === 0)
    return ''

  return `- ${exercise.weight} ${getRunWeightUnit(exercise.weightType)}`
}

const getWeight = (exercise) => {
  if (!exercise.weight || exercise.weight === 0) return ''

  return `- ${exercise.weight} ${getWeightUnit(exercise.weightType)}`
}

const getEmomLines = (block) => {
  const numberOfRounds = block.customAttributes.find(x => x.name === "rounds")?.value
  const roundTime = block.customAttributes.find(x => x.name === "roundTime")?.value
  let lines = []

  lines.push({
    name: `- Every ${formatTime(roundTime)} minutes - ${numberOfRounds} Rounds -`,
    descriptions: []
  });

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.toLowerCase()} ${getWeight(exercise)}`]
    })});

  return lines;
}

const getSuperSetLines = (block) => {
  const numberOfRounds = block.customAttributes.find(x => x.name === "sets")?.value
  const restTime = block.customAttributes.find(x => x.name === "interval")?.value
  let lines = []

  lines.push({
    name: `- ${numberOfRounds} Rounds -`,
    descriptions: []
  });
  lines.push({
    name: `Rest Time: ${formatTime(restTime)}`,
    descriptions: []
  });

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.toLowerCase()} ${getWeight(exercise)}`]
    })
  });

  return lines;
}

const getStrengthLines = (block) => {
  const numberOfRounds = block.customAttributes.find(x => x.name === "sets")?.value
  const restTime = block.customAttributes.find(x => x.name === "interval")?.value
  let lines = []

  lines.push({
    name: `- ${numberOfRounds} Rounds -`,
    descriptions: []
  });
  lines.push({
    name: `Rest Time: ${formatTime(restTime)}`,
    descriptions: []
  });

  let i = 1;

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${i}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.toLowerCase()} ${getWeight(exercise)}`]
    })
    i = i + 1;
  });

  return lines;
};

const getRftLines = (block) => {
  const numberOfRounds = block.customAttributes.find(x => x.name === "rounds")?.value
  let lines = []

  lines.push({
    name: `- ${numberOfRounds} Rounds -`,
    descriptions: []
  });

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.toLowerCase()} ${getWeight(exercise)}`]
    })});

  return lines;
};

const getAmrapLines = (block) => {
  const totalTime = block.customAttributes.find(x => x.name === "totalTime")?.value
  let lines = []
  lines.push({
    name: `- Total ${formatTime(totalTime)} minutes -`,
    descriptions: []
  })

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.toLowerCase()} ${getWeight(exercise)}`]
    })});

  return lines;
}

const getWeightUnit = (unit) => {
  if (unit === 'KG') {
    return 'kg';
  } else if (unit === 'PERCENTAGE') {
    return '%'
  }
}

const getRunWeightUnit = (unit) => {
  if (unit === 'KG') {
    return 'km/h';
  } else if (unit === 'PERCENTAGE') {
    return 'min/km'
  }
}

const intervalBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getIntervalLines(block)
  }
};

const classicStrengthBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getClassicStrengthLines(block)
  };
}


const pilatesRBlock = (block) => {
  return {
    title: getBlockTitle(block),
    lines: getPilatesRLines(block)
  }
};

const getBlockTitle = (block) => {
  return `#${block.order}   ${block.customAttributes.find(x => x.name === 'block_title')?.textValue || BLOCKS[block.type].name}`;
}

const getIntervalLines = (block) => {
  const numberOfRounds = block.customAttributes.find(x => x.name === 'intervals')?.value
  const worktime = block.customAttributes.find(x => x.name === "workTime")?.value
  const restTime = block.customAttributes.find(x => x.name === 'restTime')?.value
  let lines = []
  lines.push({
    name: `- ${numberOfRounds} Rounds -`,
    descriptions: [`Work Time: ${formatTime(worktime)}`, `Rest Time: ${formatTime(restTime)}`]
  })

  block.exercises.forEach(exercise => {
    lines.push({
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.quantity} ${exercise.unit.charAt(0).toUpperCase()}${exercise.unit.slice(1).toLowerCase()} ${getWeight(exercise)}`]
  })}
  );

  return lines;
};

const getPilatesRLines = (block) => {
  return block.exercises.map(exercise => {
    return {
      name: `${exercise.exercise.title}:`,
      descriptions: [`Setting: ${exercise.setting}`, `Reps: ${exercise.reps}`, exercise.notes ? `Notes: ${exercise.notes}` : ''],
    }
  });
};

const getClassicStrengthLines = (block) => {
  return block.exercises.map(exercise => {
    return {
      name: `${exercise.exercise.title}:`,
      descriptions: [`${exercise.numberOfSets} Sets - ${exercise.quantity} Reps ${getWeight(exercise)}`,
        `Rest ${formatTime(exercise.restTime)} between sets`,]
    }
  })
}

export default useDownloadWorkoutAsPDF;
